import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Upload,
  Switch,
  Space,
  message,
  Select,
  Button,
} from 'antd';
import {
  UploadOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ServicesApi from 'libs/api/website/services';
import SimpleEditor from '../../../../../components/RichTextEditor/SimpleEditor';
import { TranslateFormLabel } from 'components/translateInputAddon';

const { TextArea } = Input;

const ServiceForm = ({ initialData, onSubmit, loading }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [service, setService] = useState(() => {
    if (initialData) {
      // Конвертуємо company_id в shopping_center_id якщо потрібно
      const { company_id, ...rest } = initialData;

      // Фільтруємо null значення
      const filteredData = {};
      Object.keys(rest).forEach((key) => {
        if (rest[key] !== null) {
          filteredData[key] = rest[key];
        }
      });

      return {
        ...filteredData,
        shopping_center_id: company_id || initialData.shopping_center_id,
      };
    }
    return {};
  });
  const [fileList, setFileList] = useState(() => {
    if (initialData && initialData.icon) {
      return [
        {
          uid: initialData.icon_id?.toString() || '-1',
          name: initialData.icon_name || 'icon',
          status: 'done',
          url: initialData.icon,
        },
      ];
    }
    return [];
  });
  const [shoppingCenters, setShoppingCenters] = useState([]);
  const [loadingShoppingCenters, setLoadingShoppingCenters] = useState(false);
  const [selectedShoppingCenter, setSelectedShoppingCenter] = useState(null);

  // Function to determine available languages based on shopping center
  const getAvailableLanguages = () => {
    if (!selectedShoppingCenter) return ['IT', 'EN'];
    
    // Check if the shopping center is Sicily (contains "torino")
    const isSicilia =
      selectedShoppingCenter.title?.toLowerCase().includes('torino') ||
      selectedShoppingCenter.name?.toLowerCase().includes('torino') ||
      selectedShoppingCenter.id === 'torino';
    
    return isSicilia ? ['IT', 'EN', 'FR'] : ['IT', 'EN'];
  };

  const availableLanguages = getAvailableLanguages();

  useEffect(() => {
    if (initialData) {
      const { company_id, ...rest } = initialData;

      const filteredData = {};
      Object.keys(rest).forEach((key) => {
        if (rest[key] !== null) {
          filteredData[key] = rest[key];
        }
      });

      const serviceData = {
        ...filteredData,
        shopping_center_id: company_id || initialData.shopping_center_id,
      };

      setService(serviceData);
      form.setFieldsValue(serviceData);

      // Set selected shopping center for language logic
      if (serviceData.shopping_center_id && shoppingCenters.length > 0) {
        const center = shoppingCenters.find(c => c.value === serviceData.shopping_center_id);
        setSelectedShoppingCenter(center);
      }
    }
  }, [initialData, form, shoppingCenters]);

  useEffect(() => {
    const loadShoppingCenters = async () => {
      setLoadingShoppingCenters(true);
      try {
        const response = await ServicesApi.getFilters();
        if (response.data) {
          setShoppingCenters(response.data.shopping_centers || []);
        }
      } catch (error) {
        console.error('Error loading shopping centers:', error);
        message.error(t('Failed to load shopping centers'));
      } finally {
        setLoadingShoppingCenters(false);
      }
    };
    loadShoppingCenters();
  }, [t]);

  const handleSubmit = async (values) => {
    const formData = new FormData();

    // Add text fields
    Object.keys(values).forEach((key) => {
      if (key !== 'icon') {
     
        const value = values[key] || '';
        formData.append(`service[${key}]`, value);
      }
    });

    // Add icon if present and changed
    if (fileList.length > 0) {
      if (fileList[0].originFileObj) {
        // If new file is uploaded
        formData.append('service[icon]', fileList[0].originFileObj);
      } else if (initialData?.icon_id) {
        // If using existing file
        formData.append('service[icon_id]', initialData.icon_id);
      }
    }

    await onSubmit(formData);
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    // Validate file size (max 20MB)
    if (newFileList.length > 0) {
      const file = newFileList[0].originFileObj;
      if (file && file.size > 20 * 1024 * 1024) {
        message.error(t('File size cannot exceed 20MB'));
        return;
      }
    }
    setFileList(newFileList);
  };

  return (
    <Form
      form={form}
      layout='vertical'
      onFinish={handleSubmit}
      initialValues={service}
      style={{ padding: '24px' }}
    >
      <Form.Item
        name='title_it'
        label={
          <TranslateFormLabel
            label={t('Titolo (Italiano)')}
            form={form}
            sourceField='title_it'
            textKey='title'
            targetFields={{ en: 'title_en', fr: 'title_fr' }}
          />
        }
        rules={[
          {
            required: true,
            message: t('Il campo non può essere lasciato in bianco'),
          },
        ]}
      >
        <Input placeholder={t('Inserisci il titolo in italiano')} />
      </Form.Item>

      <Form.Item name='title_en' label={t('Titolo (Inglese)')}>
        <Input placeholder={t('Inserisci il titolo in inglese')} />
      </Form.Item>

      {availableLanguages.includes('FR') && (
        <Form.Item name='title_fr' label={t('Titolo (Francese)')}>
          <Input placeholder={t('Inserisci il titolo in francese')} />
        </Form.Item>
      )}

      <Form.Item
        name='description_it'
        label={
          <TranslateFormLabel
            label={t('Descrizione (Italiano)')}
            form={form}
            sourceField='description_it'
            textKey='description'
            targetFields={{
              en: 'description_en',
              fr: 'description_fr',
            }}
          />
        }
        // rules={[{ required: true, message: t('Il campo non può essere lasciato in bianco') }]}
      >
        <SimpleEditor
          placeholder={t('Inserisci la descrizione in italiano')}
          rows={4}
        />
      </Form.Item>

      <Form.Item name='description_en' label={t('Descrizione (Inglese)')}>
        <SimpleEditor
          placeholder={t('Inserisci la descrizione in inglese')}
          rows={4}
        />
      </Form.Item>

      {availableLanguages.includes('FR') && (
        <Form.Item name='description_fr' label={t('Descrizione (Francese)')}>
          <SimpleEditor
            placeholder={t('Inserisci la descrizione in francese')}
            rows={4}
          />
        </Form.Item>
      )}

      <Form.Item
        name='icon'
        label={t('Icona')}
        rules={[
          {
            required: !initialData?.icon,
            message: t('Il campo non può essere lasciato in bianco'),
          },
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Upload
            accept='image/*'
            maxCount={1}
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={() => false}
          >
            <Button icon={<UploadOutlined />}>{t('Carica file')}</Button>
          </Upload>
        </div>
      </Form.Item>

      <Form.Item
        name='shopping_center_id'
        label={t('Outlet Village')}
        rules={[
          {
            required: true,
            message: t('Il campo non può essere lasciato in bianco'),
          },
        ]}
      >
        <Select
          loading={loadingShoppingCenters}
          placeholder={t('Seleziona shopping center')}
          allowClear
          onChange={(value) => {
            const center = shoppingCenters.find(c => c.value === value);
            setSelectedShoppingCenter(center);
          }}
        >
          {shoppingCenters.map((center) => (
            <Select.Option key={center.value} value={center.value}>
              {center.title}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name='reservation_required'
        label={t('Reservation required')}
        valuePropName='checked'
        initialValue={false}
      >
        <Switch defaultChecked={false} />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button
            type='primary'
            htmlType='submit'
            icon={<SaveOutlined />}
            loading={loading}
            style={{ backgroundColor: '#002637', borderColor: '#002637' }}
          >
            {t('Salva')}
          </Button>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('../')}>
            {t('Torna indietro')}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default ServiceForm;
